/**
 * Invoices Page
 *
 * Invoice management with list, create, edit, and view operations.
 * Uses accounting store to fetch data from database via GraphQL.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useFilteredInvoices } from '@business-assistant/shared/hooks';
import type { Invoice, InvoiceStatus } from '@business-assistant/shared/data/types';
import type { CentsAmount } from '@business-assistant/shared';
import { InvoiceForm } from '../forms';
import type { InvoiceFormData } from '../forms';
import { useAccountingStore } from '../../state/accountingState';
import type { Invoice as StoreInvoice } from '../../types/core';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface InvoicesPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ViewMode = 'list' | 'create' | 'edit' | 'detail';

// =============================================================================
// CONSTANTS
// =============================================================================

const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, { label: string }> = {
  draft: { label: 'Bozza' },
  sent: { label: 'Inviata' },
  paid: { label: 'Pagata' },
  overdue: { label: 'Scaduta' },
  cancelled: { label: 'Annullata' },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// =============================================================================
// COMPONENTS
// =============================================================================

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const config = INVOICE_STATUS_CONFIG[status];
  return (
    <span>
      {config.label}
    </span>
  );
}

function InvoiceListItem({
  invoice,
  onClick,
  onEdit,
  onDelete,
}: {
  invoice: Invoice;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="accounting-card--row"
    >
      {/* Icon */}
      <div className="accounting-icon--md accounting-flex--center">
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      {/* Info */}
      <div className="accounting-content">
        <div className="accounting-flex--row accounting-flex--gap-sm">
          <span>{invoice.id.toUpperCase()}</span>
          <StatusBadge status={invoice.status} />
        </div>
        <div>{invoice.customerName}</div>
        <div>
          Emessa: {formatDate(invoice.date)} | Scadenza: {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
        </div>
      </div>

      {/* Amount */}
      <div className="accounting-content--right">
        <div>{formatCurrency(invoice.total)}</div>
        {(invoice as any).paidAmount > 0 && (invoice as any).paidAmount < invoice.total && (
          <div>
            Pagato: {formatCurrency((invoice as any).paidAmount || 0)}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="accounting-actions" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onEdit}
          title="Modifica"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          onClick={onDelete}
          title="Elimina"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

function InvoiceDetail({
  invoice,
  onBack,
  onEdit,
}: {
  invoice: Invoice;
  onBack: () => void;
  onEdit: () => void;
}) {
  return (
    <div>
      {/* Header */}
      <div className="accounting-flex--row accounting-flex--gap-lg">
        <button onClick={onBack}>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="accounting-content">
          <div className="accounting-flex--row accounting-flex--gap-md">
            <h2>Fattura {invoice.id.toUpperCase()}</h2>
            <StatusBadge status={invoice.status} />
          </div>
          <p>{invoice.customerName}</p>
        </div>
        <button onClick={onEdit} className="accounting-flex--row accounting-flex--gap-sm">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Modifica
        </button>
      </div>

      {/* Invoice Info */}
      <div className="accounting-grid--3col">
        <div>
          <div>Data emissione</div>
          <div>{formatDate(invoice.date)}</div>
        </div>
        <div>
          <div>Data scadenza</div>
          <div>{invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}</div>
        </div>
        {(invoice as any).customerVat && (
          <div>
            <div>P.IVA Cliente</div>
            <div>{(invoice as any).customerVat}</div>
          </div>
        )}
      </div>

      {/* Line Items */}
      <div>
        <table className="accounting-table">
          <thead>
            <tr>
              <th>Descrizione</th>
              <th>Qta</th>
              <th>Prezzo</th>
              <th>IVA</th>
              <th>Totale</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(item.unitPrice)}</td>
                <td>{Math.round(item.vatRate * 100)}%</td>
                <td>{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="accounting-totals">
        <div className="accounting-totals__content">
          <div className="accounting-totals__row">
            <span>Imponibile</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="accounting-totals__row">
            <span>IVA</span>
            <span>{formatCurrency(invoice.vatTotal)}</span>
          </div>
          <div className="accounting-totals__row">
            <span>Totale</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
          {(invoice as any).paidAmount > 0 && (
            <div className="accounting-totals__row">
              <span>Pagato</span>
              <span>{formatCurrency((invoice as any).paidAmount)}</span>
            </div>
          )}
          {((invoice as any).paidAmount || 0) < invoice.total && invoice.status !== 'paid' && (
            <div className="accounting-totals__row">
              <span>Da pagare</span>
              <span>{formatCurrency(invoice.total - ((invoice as any).paidAmount || 0))}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function InvoicesPage({ onNavigate, onBack }: InvoicesPageProps) {
  // URL parameter management
  const { params, filters, setParam, clearParam, hasActiveFilters } = useSearchParams();

  // Get invoices from store (fetched from database via GraphQL)
  const {
    invoices: storeInvoices,
    loadInvoices,
    loading,
  } = useAccountingStore();

  // Load invoices on mount
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Map store invoices to the Invoice type used by this component
  const invoices: Invoice[] = storeInvoices.map((inv: StoreInvoice) => ({
    id: inv.id,
    number: inv.id.replace('inv-', 'INV-').toUpperCase(),
    customerId: inv.customerId || '',
    customerName: inv.customerName,
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    status: inv.status,
    items: inv.items.map(item => ({
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      vatRate: item.vatRate,
      amount: item.grossAmount,
    })),
    subtotal: inv.netTotal,
    vatAmount: inv.vatTotal,
    total: inv.grossTotal,
    paidAmount: inv.paidAmount,
    notes: inv.notes,
    createdAt: inv.createdAt,
    updatedAt: inv.updatedAt,
  }));

  // Local state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Use filtered invoices hook with status filter
  const { data: filteredInvoices, filteredCount, totalCount, isFiltered } = useFilteredInvoices(
    invoices.filter((inv) => {
      if (params.status && params.status !== 'all') {
        return inv.status === params.status;
      }
      return true;
    }),
    filters
  );

  // Handle search input
  const handleSearch = useCallback((query: string) => {
    if (query.trim()) {
      setParam('s', query);
    } else {
      clearParam('s');
    }
  }, [setParam, clearParam]);

  // Handle status filter
  const handleStatusFilter = useCallback((status: string) => {
    if (status === 'all') {
      clearParam('status');
    } else {
      setParam('status', status);
    }
  }, [setParam, clearParam]);

  // Handle invoice click - update URL with invoice ID
  const handleInvoiceClick = useCallback((invoice: Invoice) => {
    setParam('invoice', invoice.id);
    setSelectedInvoice(invoice);
    setViewMode('detail');
  }, [setParam]);

  // Handle delete invoice
  const { deleteInvoice } = useAccountingStore();
  const handleDeleteInvoice = (invoice: Invoice) => {
    if (confirm(`Eliminare la fattura "${invoice.number}"?`)) {
      deleteInvoice(invoice.id);
    }
  };

  // Clear filters
  const handleClearFilters = useCallback(() => {
    clearParam('s');
    clearParam('status');
    clearParam('customer');
    clearParam('project');
  }, [clearParam]);

  return (
    <div className="accounting-page accounting-page--narrow">
      {viewMode === 'list' && (
        <>
          {/* Toolbar */}
          <div className="accounting-toolbar">
            <div className="accounting-toolbar__search">
              <input
                type="text"
                placeholder="Cerca fattura..."
                defaultValue={params.s || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <select
              value={params.status || 'all'}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <option value="all">Tutti gli stati</option>
              <option value="draft">Bozza</option>
              <option value="sent">Inviata</option>
              <option value="paid">Pagata</option>
              <option value="overdue">Scaduta</option>
              <option value="cancelled">Annullata</option>
            </select>
            {hasActiveFilters && (
              <button onClick={handleClearFilters} className="accounting-flex--row accounting-flex--gap-sm">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Rimuovi filtri
              </button>
            )}
            <button
              onClick={() => setViewMode('create')}
              className="accounting-flex--row accounting-flex--gap-sm"
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuova Fattura
            </button>
          </div>

          {/* Invoice List */}
          {filteredInvoices.length === 0 ? (
            <div className="accounting-empty">
              <svg
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p>Nessuna fattura trovata</p>
            </div>
          ) : (
            <div className="accounting-list">
              {/* Filter results count */}
              {isFiltered && (
                <div>
                  Mostrate {filteredCount} di {totalCount} fatture
                </div>
              )}
              {filteredInvoices.map((invoice) => (
                <InvoiceListItem
                  key={invoice.id}
                  invoice={invoice}
                  onClick={() => handleInvoiceClick(invoice)}
                  onEdit={() => {
                    setSelectedInvoice(invoice);
                    setViewMode('edit');
                  }}
                  onDelete={() => handleDeleteInvoice(invoice)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {viewMode === 'detail' && selectedInvoice && (
        <InvoiceDetail
          invoice={selectedInvoice}
          onBack={() => {
            setViewMode('list');
            setSelectedInvoice(null);
          }}
          onEdit={() => setViewMode('edit')}
        />
      )}

      {viewMode === 'create' && (
        <InvoiceForm
          isOpen={true}
          onSave={async (data: InvoiceFormData) => {
            // Create new invoice via store (saves to database)
            const { createInvoice } = useAccountingStore.getState();
            await createInvoice({
              customerId: data.customerId,
              customerName: data.customerName,
              issueDate: data.issueDate,
              dueDate: data.dueDate,
              status: data.status,
              items: data.items.map((item) => ({
                description: item.description,
                quantity: item.quantity,
                unitPrice: Math.round(item.unitPrice * 100) as CentsAmount,
                vatRate: item.vatRate,
                netAmount: Math.round(item.quantity * item.unitPrice * 100) as CentsAmount,
                vatAmount: Math.round(item.quantity * item.unitPrice * item.vatRate * 100) as CentsAmount,
                grossAmount: Math.round(item.quantity * item.unitPrice * (1 + item.vatRate) * 100) as CentsAmount,
              })),
              netTotal: Math.round(data.netTotal * 100) as CentsAmount,
              vatTotal: Math.round(data.vatTotal * 100) as CentsAmount,
              grossTotal: Math.round(data.grossTotal * 100) as CentsAmount,
              paidAmount: 0 as CentsAmount,
              notes: data.notes,
            });
            setViewMode('list');
          }}
          onCancel={() => setViewMode('list')}
        />
      )}

      {viewMode === 'edit' && selectedInvoice && (
        <InvoiceForm
          isOpen={true}
          invoice={selectedInvoice}
          onSave={async (data: InvoiceFormData) => {
            // Update existing invoice via store (saves to database)
            const { updateInvoice } = useAccountingStore.getState();
            await updateInvoice(selectedInvoice.id, {
              customerId: data.customerId,
              customerName: data.customerName,
              issueDate: data.issueDate,
              dueDate: data.dueDate,
              status: data.status,
              items: data.items.map((item) => ({
                description: item.description,
                quantity: item.quantity,
                unitPrice: Math.round(item.unitPrice * 100) as CentsAmount,
                vatRate: item.vatRate,
                netAmount: Math.round(item.quantity * item.unitPrice * 100) as CentsAmount,
                vatAmount: Math.round(item.quantity * item.unitPrice * item.vatRate * 100) as CentsAmount,
                grossAmount: Math.round(item.quantity * item.unitPrice * (1 + item.vatRate) * 100) as CentsAmount,
              })),
              netTotal: Math.round(data.netTotal * 100) as CentsAmount,
              vatTotal: Math.round(data.vatTotal * 100) as CentsAmount,
              grossTotal: Math.round(data.grossTotal * 100) as CentsAmount,
              notes: data.notes,
            });
            setSelectedInvoice(null);
            setViewMode('list');
          }}
          onCancel={() => {
            setSelectedInvoice(null);
            setViewMode('list');
          }}
        />
      )}
    </div>
  );
}

export type { InvoicesPageProps };
